/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { externalKeysUpdateGcpKms } from "../funcs/externalKeysUpdateGcpKms.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GcpKmsKey } from "../models/components/gcpkmskey.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpdateGcpKmsKeyRequest,
  UpdateGcpKmsKeySecurity,
} from "../models/operations/updategcpkmskey.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UpdateGcpKmsKeyMutationVariables = {
  request: UpdateGcpKmsKeyRequest;
  security?: UpdateGcpKmsKeySecurity | undefined;
  options?: RequestOptions;
};

export type UpdateGcpKmsKeyMutationData = GcpKmsKey;

export type UpdateGcpKmsKeyMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * updateGcpKmsKey externalKeys
 *
 * @remarks
 * Update a GCP KMS external key's name, backing credential and customer grant reference. Requires org:admin. These three fields are replaced, not patched: omitting the optional customer_grant_reference clears it. The resource name and algorithm are immutable: an external key identifies exactly one signable crypto key version permanently, so changing what the key is means deleting it and creating a new one. The backing credential stays editable because repairing the path to a key does not change the key material Speakeasy signs with.
 */
export function useUpdateGcpKmsKeyMutation(
  options?: MutationHookOptions<
    UpdateGcpKmsKeyMutationData,
    UpdateGcpKmsKeyMutationError,
    UpdateGcpKmsKeyMutationVariables
  >,
): UseMutationResult<
  UpdateGcpKmsKeyMutationData,
  UpdateGcpKmsKeyMutationError,
  UpdateGcpKmsKeyMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUpdateGcpKmsKeyMutation(client, options),
    ...options,
  });
}

export function mutationKeyUpdateGcpKmsKey(): MutationKey {
  return ["@gram/client", "externalKeys", "updateGcpKms"];
}

export function buildUpdateGcpKmsKeyMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UpdateGcpKmsKeyMutationVariables,
  ) => Promise<UpdateGcpKmsKeyMutationData>;
} {
  return {
    mutationKey: mutationKeyUpdateGcpKmsKey(),
    mutationFn: function updateGcpKmsKeyMutationFn({
      request,
      security,
      options,
    }): Promise<UpdateGcpKmsKeyMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(externalKeysUpdateGcpKms(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
